import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  fetchMembershipApplications,
  fetchApplicationById,
  approveApplication,
  rejectApplication,
  verifyApplication,
  deleteApplication,
} from "../../../api/membershipApplicationsApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import { GlobalButton } from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryApprovalsSection,
} from "../../../../../global/directory";
import GlobalFeedback from "../../../../../global/GlobalFeedback";
import { GlobalModal } from "../../../../../global/GlobalModal";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";
import "../../../../user-builder/styles/userBuilder.css";
import "../../../../user-builder/styles/userBuilderTable.css";
import "../../../../user-builder/styles/userBuilderForms.css";
import "../../../styles/MembershipApplications.css";

const PAGE_SIZE = 10;
const COLUMN_VISIBILITY_KEY = "girinestham.membershipApplications.columnVisibility";

const defaultUiFilters = () => ({
  student_search: "",
  column: "",
  value: "",
});

function loadSavedVisibility() {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

// Column definitions for visibility picker
const COLUMN_DEFS = [
  { id: "select", label: "Select", defaultVisible: true, isAction: true },
  { id: "application_code", label: "Application Code", defaultVisible: true },
  { id: "student_name", label: "Student", defaultVisible: true },
  { id: "plan_name", label: "Plan", defaultVisible: true },
  { id: "amount", label: "Amount", defaultVisible: true },
  { id: "start_date", label: "Start date", defaultVisible: true },
  { id: "end_date", label: "End date", defaultVisible: true },
  { id: "application_status", label: "Status", defaultVisible: true },
  { id: "application_verified", label: "Application verified", defaultVisible: true },
  { id: "payment_verified", label: "Payment verified", defaultVisible: true },
  { id: "submitted_at", label: "Submitted", defaultVisible: true },
  { id: "actions", label: "Actions", defaultVisible: true, isAction: true },
];

const COLUMN_DEFS_FOR_FILTERS = COLUMN_DEFS.filter((c) => !c.isAction);
const SORTABLE_APPLICATION_COLUMNS = new Set([
  "application_code",
  "student_name",
  "amount",
  "submitted_at",
]);

// Validation helpers
const validateRemarks = (value, isRequired = false) => {
  if (isRequired && (!value || !String(value).trim())) {
    return "Remarks are required";
  }
  if (value && String(value).trim().length < 5) {
    return "Remarks must be at least 5 characters";
  }
  return "";
};

const validateRejectionReason = (value) => {
  if (String(value).trim().length > 500) {
    return "Rejection reason must not exceed 500 characters";
  }
  return "";
};

// Format helpers
const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);
};

const getStatusBadgeClass = (status) => {
  const statusLower = String(status).toLowerCase();
  if (statusLower === "approved") return "status-badge status-badge--success";
  if (statusLower === "rejected") return "status-badge status-badge--danger";
  if (statusLower === "pending") return "status-badge status-badge--warning";
  return "status-badge";
};

const getPaymentStatusBadgeClass = (verified) =>
  verified ? "status-badge status-badge--success" : "status-badge status-badge--warning";

export default function MembershipApplicationsDirectorySection({
  canEdit = false,
  canDelete = false,
}) {
  // State - Data
  const [applications, setApplications] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // State - Selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [sortState, setSortState] = useState({
    sortBy: "submitted_at",
    sortOrder: "DESC",
  });
  const [draftUiFilters, setDraftUiFilters] = useState(defaultUiFilters);
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadSavedVisibility();
    const initial = {};
    for (const c of COLUMN_DEFS) initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    return initial;
  });

  // State - Loading
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // State - Feedback
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [successTitle, setSuccessTitle] = useState("Success");
  const [successTone, setSuccessTone] = useState("success");
  const [fieldErrors, setFieldErrors] = useState({});

  // State - Modals
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);

  // State - Form Data
  const [approveRemarks, setApproveRemarks] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    setPage(1);
  }, [sortState.sortBy, sortState.sortOrder]);

  // Fetch applications
  const loadApplications = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchMembershipApplications({
        page,
        limit: PAGE_SIZE,
        sortBy: sortState.sortBy,
        sortOrder: sortState.sortOrder,
      });
      setApplications(result.applications);
      setTotal(Number(result.meta?.total || 0));
      setTotalPages(Math.max(1, Number(result.meta?.totalPages || 1)));
      setPage(Number(result.meta?.page || page));
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message || "Failed to load applications");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [page, sortState.sortBy, sortState.sortOrder]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleSort = useCallback((column) => {
    setSortState((prev) => ({
      sortBy: column,
      sortOrder:
        prev.sortBy === column && prev.sortOrder === "ASC" ? "DESC" : "ASC",
    }));
  }, []);

  const visibleColumns = useMemo(
    () => COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  const valueFilterOptions = useMemo(() => {
    if (!draftUiFilters.column) return [];
    const seen = new Set();
    const opts = [];
    for (const row of applications) {
      let value;
      if (draftUiFilters.column === "application_verified" || draftUiFilters.column === "payment_verified") {
        value = row[draftUiFilters.column] ? "Verified" : "Not verified";
      } else {
        const raw = row[draftUiFilters.column];
        if (raw == null || raw === "") continue;
        value = String(raw);
      }
      if (!seen.has(value)) {
        seen.add(value);
        opts.push({ value, label: value });
      }
    }
    return opts;
  }, [draftUiFilters.column, applications]);

  const filteredApplications = useMemo(() => {
    const search = String(draftUiFilters.student_search || "").trim().toLowerCase();
    const col = draftUiFilters.column;
    const val = String(draftUiFilters.value || "").trim();
    return applications.filter((app) => {
      if (search) {
        const target = String(app.student_name || "").toLowerCase();
        if (!target.includes(search)) return false;
      }
      if (col && val) {
        if (col === "application_verified" || col === "payment_verified") {
          const flag = app[col] ? "Verified" : "Not verified";
          return flag === val;
        }
        return String(app[col] ?? "") === val;
      }
      return true;
    });
  }, [applications, draftUiFilters]);

  const getApplicationsExportData = useCallback(() => {
    const exportColumns = visibleColumns.filter(
      (c) => c.id !== "select" && c.id !== "actions",
    );
    const headers = exportColumns.map((c) => c.label);
    const rows = filteredApplications.map((app) =>
      exportColumns.map((c) => {
        if (c.id === "application_verified") return app.application_verified ? "Yes" : "No";
        if (c.id === "payment_verified") return app.payment_verified ? "Yes" : "No";
        if (c.id === "start_date") return formatDate(app.start_date);
        if (c.id === "end_date") return formatDate(app.end_date);
        if (c.id === "submitted_at") return formatDate(app.submitted_at);
        if (c.id === "amount") return String(app.amount ?? "");
        return String(app[c.id] ?? "");
      }),
    );
    return { headers, rows };
  }, [visibleColumns, filteredApplications]);

  // Handlers - Selection
  const toggleSelectAll = useCallback(() => {
    if (filteredApplications.length === 0) return;
    const allIds = new Set(filteredApplications.map((app) => app.application_id));
    const allSelected = [...allIds].every((id) => selectedIds.has(id));

    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(allIds);
    }
  }, [filteredApplications, selectedIds]);

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Handlers - View Details
  const handleViewDetails = async (application) => {
    setSelectedApplication(application);
    setShowDetailModal(true);
    setLoadingDetails(true);
    setError("");

    try {
      const details = await fetchApplicationById(application.application_id);
      setSelectedApplication(details);
    } catch (err) {
      setError(err.message || "Failed to load application details");
      setShowDetailModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handlers - Single Approval
  const handleOpenApprove = (application) => {
    setSelectedApplication(application);
    setApproveRemarks("");
    setFieldErrors({});
    setShowApproveModal(true);
  };

  const handleApprove = async () => {
    const error = validateRemarks(approveRemarks, false);
    if (error) {
      setFieldErrors({ remarks: error });
      return;
    }

    setProcessing(true);
    setError("");

    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      const approveRes = await approveApplication(
        selectedApplication.application_id,
        { remarks: approveRemarks.trim() || undefined },
        csrfToken,
      );

      setSuccess(approveRes?.message || "Application approved successfully");
      setShowApproveModal(false);
      setApproveRemarks("");
      setSelectedApplication(null);
      loadApplications();

    } catch (err) {
      setError(err.message || "Failed to approve application");
      if (err.details) {
        setFieldErrors(err.details);
      }
    } finally {
      setProcessing(false);
    }
  };

  // Handlers - Single Rejection
  const handleOpenReject = (application) => {
    setSelectedApplication(application);
    setRejectReason("");
    setFieldErrors({});
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    const error = validateRejectionReason(rejectReason);
    if (error) {
      setFieldErrors({ reason: error });
      return;
    }

    setProcessing(true);
    setError("");

    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      const rejectRes = await rejectApplication(
        selectedApplication.application_id,
        { reason: rejectReason.trim() },
        csrfToken,
      );

      setSuccess(rejectRes?.message || "Application rejected successfully");
      setShowRejectModal(false);
      setRejectReason("");
      setSelectedApplication(null);
      loadApplications();

    } catch (err) {
      setError(err.message || "Failed to reject application");
      if (err.details) {
        setFieldErrors(err.details);
      }
    } finally {
      setProcessing(false);
    }
  };

  // Handlers - Bulk Approval
  const handleOpenBulkApprove = () => {
    if (selectedIds.size === 0) return;
    setApproveRemarks("");
    setFieldErrors({});
    setShowBulkApproveModal(true);
  };

  const handleBulkApprove = async () => {
    const error = validateRemarks(approveRemarks, false);
    if (error) {
      setFieldErrors({ remarks: error });
      return;
    }

    setProcessing(true);
    setError("");

    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      const ids = Array.from(selectedIds);
      if (!ids.length) {
        setError("Select at least one application to approve.");
        setProcessing(false);
        return;
      }
      const results = await Promise.allSettled(
        ids.map((id) =>
          approveApplication(
            id,
            { remarks: approveRemarks.trim() || undefined },
            csrfToken,
          )
        )
      );

      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.filter((r) => r.status === "rejected").length;

      if (failed === 0) {
        setSuccess(`Successfully approved ${succeeded} application(s)`);
      } else if (succeeded === 0) {
        setError(`Failed to approve all ${failed} application(s)`);
      } else {
        setSuccess(`Approved ${succeeded} application(s), ${failed} failed`);
      }

      setShowBulkApproveModal(false);
      setApproveRemarks("");
      setSelectedIds(new Set());
      loadApplications();

    } catch (err) {
      setError(err.message || "Failed to approve applications");
    } finally {
      setProcessing(false);
    }
  };

  // Handlers - Bulk Rejection
  const handleOpenBulkReject = () => {
    if (selectedIds.size === 0) return;
    setRejectReason("");
    setFieldErrors({});
    setShowBulkRejectModal(true);
  };

  const handleBulkReject = async () => {
    const error = validateRejectionReason(rejectReason);
    if (error) {
      setFieldErrors({ reason: error });
      return;
    }

    setProcessing(true);
    setError("");

    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      const ids = Array.from(selectedIds);
      if (!ids.length) {
        setError("Select at least one application to reject.");
        setProcessing(false);
        return;
      }
      const results = await Promise.allSettled(
        ids.map((id) =>
          rejectApplication(
            id,
            { reason: rejectReason.trim() },
            csrfToken,
          )
        )
      );

      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.filter((r) => r.status === "rejected").length;

      if (failed === 0) {
        setSuccess(`Successfully rejected ${succeeded} application(s)`);
      } else if (succeeded === 0) {
        setError(`Failed to reject all ${failed} application(s)`);
      } else {
        setSuccess(`Rejected ${succeeded} application(s), ${failed} failed`);
      }

      setShowBulkRejectModal(false);
      setRejectReason("");
      setSelectedIds(new Set());
      loadApplications();

    } catch (err) {
      setError(err.message || "Failed to reject applications");
    } finally {
      setProcessing(false);
    }
  };

  // Clear field error on change
  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Derived state
  const canApproveOrReject = (application) => {
    return Boolean(application?.application_id);
  };

  const selectedCount = selectedIds.size;
  const hasSelection = selectedCount > 0;

  const handleBulkToggleApplicationVerified = async (verified) => {
    if (!selectedIds.size) return;
    setProcessing(true);
    setError("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      const ids = Array.from(selectedIds);
      const results = await Promise.allSettled(
        ids.map((id) =>
          verifyApplication(id, { verified }, csrfToken)
        )
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) {
        setSuccessTitle("Success");
        setSuccessTone("success");
        setSuccess(
          verified
            ? `Marked ${succeeded} application(s) verified`
            : `Marked ${succeeded} application(s) not verified`
        );
      } else if (succeeded === 0) {
        setSuccessTitle("Update Failed");
        setSuccessTone("warning");
        setSuccess(
          verified
            ? `Failed to mark all ${failed} application(s) verified`
            : `Failed to mark all ${failed} application(s) not verified`
        );
      } else {
        setSuccessTitle("Partial Update");
        setSuccessTone("warning");
        setSuccess(
          verified
            ? `Marked ${succeeded} verified, ${failed} failed`
            : `Marked ${succeeded} not verified, ${failed} failed`
        );
      }
      setSelectedIds(new Set());
      await loadApplications();
    } catch (err) {
      setError(err.message || "Failed to update selected application verifications");
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteApplication = async (application) => {
    if (!window.confirm(`Delete application ${application.application_code}?`)) return;
    setProcessing(true);
    setError("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteApplication(application.application_id, csrfToken);
      setSuccess("Application deleted successfully");
      await loadApplications();
    } catch (err) {
      setError(err.message || "Failed to delete application");
    } finally {
      setProcessing(false);
    }
  };

  const applicationsTableHeaders = useMemo(
    () =>
      visibleColumns.map((col) => {
        if (col.id === "select") {
          return {
            key: col.id,
            className: "checkbox-cell",
            label: (
              <input
                type="checkbox"
                onChange={toggleSelectAll}
                checked={
                  filteredApplications.length > 0 &&
                  filteredApplications.every((app) =>
                    selectedIds.has(app.application_id),
                  )
                }
                ref={(el) => {
                  if (el) {
                    el.indeterminate =
                      selectedIds.size > 0 &&
                      selectedIds.size < filteredApplications.length;
                  }
                }}
                disabled={filteredApplications.length === 0}
                aria-label="Select all applications"
              />
            ),
          };
        }
        if (col.id === "actions") {
          return { key: col.id, label: "Actions" };
        }
        if (SORTABLE_APPLICATION_COLUMNS.has(col.id)) {
          return {
            key: col.id,
            label: (
              <button
                type="button"
                className="sortable"
                onClick={() => handleSort(col.id)}
                style={{
                  margin: 0,
                  padding: 0,
                  font: "inherit",
                  fontWeight: 600,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                {col.label}
                {sortState.sortBy === col.id ? (
                  <span>
                    {sortState.sortOrder === "ASC" ? " ↑" : " ↓"}
                  </span>
                ) : null}
              </button>
            ),
          };
        }
        return { key: col.id, label: col.label };
      }),
    [
      visibleColumns,
      filteredApplications,
      selectedIds,
      sortState.sortBy,
      sortState.sortOrder,
      toggleSelectAll,
      handleSort,
    ],
  );

  if (initialLoading) {
    return (
      <div
        id="ms-applications"
        className="user-builder-panel"
        style={{ scrollMarginTop: "2rem" }}
      >
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Membership Applications</h3>
        </div>
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      id="ms-applications"
      className="user-builder-panel"
      style={{ scrollMarginTop: "2rem" }}
    >
      <div className="user-builder-panel__head" style={{ marginBottom: "0.35rem" }}>
        <h3 className="user-builder-panel__title">Membership Applications</h3>
        <p className="user-builder-panel__hint">
          Review and process pending membership applications submitted by
          volunteers.
        </p>
      </div>

      <GlobalSuccessFlashModal
        message={success}
        title={successTitle}
        tone={successTone}
        onDismiss={() => {
          setSuccess("");
          setSuccessTitle("Success");
          setSuccessTone("success");
        }}
      />
      <GlobalFeedback type="error" text={error} />

      <GlobalDirectoryShell
        filters={
          <GlobalDirectoryFiltersSection
            className="ub-filters"
            searchLabel="Search by student name"
            searchPlaceholder="Type student name"
            searchValue={draftUiFilters.student_search}
            onSearchChange={(v) =>
              setDraftUiFilters((f) => ({ ...f, student_search: v }))
            }
            columnLabel="Column"
            columnValue={draftUiFilters.column}
            onColumnChange={(v) =>
              setDraftUiFilters((f) => ({ ...f, column: v, value: "" }))
            }
            gridColumnDefs={COLUMN_DEFS_FOR_FILTERS}
            valueLabel="Value"
            valueValue={draftUiFilters.value}
            onValueChange={(v) => setDraftUiFilters((f) => ({ ...f, value: v }))}
            valueOptions={valueFilterOptions}
            onClear={() => setDraftUiFilters(defaultUiFilters())}
            clearFilterLabel="Clear filter"
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={setColumnVisibility}
                storageKey={COLUMN_VISIBILITY_KEY}
              />
            }
            secondaryEnd={
              <GlobalDirectoryExportMenu
                getExportData={getApplicationsExportData}
                filenameBase={`membership_applications_${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`}
              />
            }
          />
        }
        approvals={
          <GlobalDirectoryApprovalsSection
            title="Approval & Marking"
            hint={
              hasSelection
                ? `${selectedCount} selected`
                : "Select checkboxes to mark in bulk"
            }
            actions={
              <>
                {canEdit ? (
                  <>
                <GlobalButton
                  variant="ghost"
                  size="sm"
                  onClick={() => handleBulkToggleApplicationVerified(true)}
                  disabled={processing || !hasSelection}
                >
                  Mark App Verified
                </GlobalButton>
                <GlobalButton
                  variant="ghost"
                  size="sm"
                  onClick={() => handleBulkToggleApplicationVerified(false)}
                  disabled={processing || !hasSelection}
                >
                  Mark App Not Verified
                </GlobalButton>
                  </>
                ) : null}
                <GlobalButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setSelectedIds(new Set())}
                  disabled={processing || !hasSelection}
                >
                  Clear Selection
                </GlobalButton>
                {canEdit ? (
                  <>
                <GlobalButton
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenBulkApprove}
                  disabled={processing || !hasSelection}
                >
                  Bulk Approve
                </GlobalButton>
                <GlobalButton
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenBulkReject}
                  disabled={processing || !hasSelection}
                >
                  Bulk Reject
                </GlobalButton>
                  </>
                ) : null}
              </>
            }
          />
        }
        grid={
          <GlobalDirectoryGridSection
            loading={loading && applications.length === 0}
            loadingMessage="Loading applications…"
            headers={applicationsTableHeaders}
            isEmpty={!loading && filteredApplications.length === 0}
            emptyMessage="No applications found"
            tableAriaLabel="Membership applications"
            wrapClassName="ub-table-wrap"
            tableClassName="ub-table"
          >
            {filteredApplications.map((app) => (
              <tr key={app.application_id}>
                {columnVisibility.select && (
                  <td className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(app.application_id)}
                      onChange={() => toggleSelectOne(app.application_id)}
                    />
                  </td>
                )}
                {columnVisibility.application_code && (
                  <td>{app.application_code}</td>
                )}
                {columnVisibility.student_name && (
                  <td>
                    <div className="student-info">
                      <span className="student-name">{app.student_name}</span>
                      <span className="student-code">{app.student_code}</span>
                    </div>
                  </td>
                )}
                {columnVisibility.plan_name && <td>{app.plan_name}</td>}
                {columnVisibility.amount && (
                  <td>{formatCurrency(app.amount)}</td>
                )}
                {columnVisibility.start_date && (
                  <td>{formatDate(app.start_date)}</td>
                )}
                {columnVisibility.end_date && (
                  <td>{formatDate(app.end_date)}</td>
                )}
                {columnVisibility.application_status && (
                  <td>
                    <span
                      className={getStatusBadgeClass(app.application_status)}
                    >
                      {app.application_status}
                    </span>
                  </td>
                )}
                {columnVisibility.application_verified && (
                  <td>
                    <span
                      className={getStatusBadgeClass(
                        app.application_verified ? "approved" : "pending",
                      )}
                    >
                      {app.application_verified ? "Verified" : "Not verified"}
                    </span>
                  </td>
                )}
                {columnVisibility.payment_verified && (
                  <td>
                    <span
                      className={getPaymentStatusBadgeClass(
                        Boolean(app.payment_verified),
                      )}
                    >
                      {app.payment_verified ? "Verified" : "Not verified"}
                    </span>
                  </td>
                )}
                {columnVisibility.submitted_at && (
                  <td>{formatDate(app.submitted_at)}</td>
                )}
                {columnVisibility.actions && (
                  <td className="ub-table__col-actions">
                    <div className="ub-table-actions">
                      <GlobalTableActionButton
                        action="view"
                        onClick={() => handleViewDetails(app)}
                      />
                      {canEdit && canApproveOrReject(app) ? (
                        <>
                          <GlobalTableActionButton
                            action="approve"
                            onClick={() => handleOpenApprove(app)}
                            disabled={processing}
                          />
                          <GlobalTableActionButton
                            action="reject"
                            onClick={() => handleOpenReject(app)}
                            disabled={processing}
                          />
                        </>
                      ) : null}
                      {canDelete ? (
                        <GlobalTableActionButton
                          action="delete"
                          onClick={() => handleDeleteApplication(app)}
                          disabled={processing}
                        />
                      ) : null}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </GlobalDirectoryGridSection>
        }
        pagination={
          <GlobalDirectoryPaginationSection
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => setPage(p)}
            label="Membership applications pagination"
          />
        }
      />

      {/* Detail Modal */}
      <GlobalModal
        open={showDetailModal}
        onClose={() => !loadingDetails && setShowDetailModal(false)}
        title="Application Details"
        size="large"
      >
        {loadingDetails ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading details...</p>
          </div>
        ) : selectedApplication ? (
          <div className="application-details">
            <div className="detail-section">
              <h4>Application Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Application Code</label>
                  <span>{selectedApplication.application_code}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <span
                    className={getStatusBadgeClass(
                      selectedApplication.application_status,
                    )}
                  >
                    {selectedApplication.application_status}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Submitted At</label>
                  <span>{formatDate(selectedApplication.submitted_at)}</span>
                </div>
                {selectedApplication.approved_at && (
                  <>
                    <div className="detail-item">
                      <label>Approved At</label>
                      <span>{formatDate(selectedApplication.approved_at)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Approved By</label>
                      <span>{selectedApplication.approved_by_name || "-"}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h4>Student Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Name</label>
                  <span>{selectedApplication.student_name}</span>
                </div>
                <div className="detail-item">
                  <label>Student Code</label>
                  <span>{selectedApplication.student_code}</span>
                </div>
                <div className="detail-item">
                  <label>Phone</label>
                  <span>{selectedApplication.student_phone || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <span>{selectedApplication.student_email || "-"}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Membership Plan</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Plan Name</label>
                  <span>{selectedApplication.plan_name}</span>
                </div>
                <div className="detail-item">
                  <label>Plan Price</label>
                  <span>{formatCurrency(selectedApplication.plan_price)}</span>
                </div>
                <div className="detail-item">
                  <label>Start Date</label>
                  <span>{formatDate(selectedApplication.start_date)}</span>
                </div>
                <div className="detail-item">
                  <label>End Date</label>
                  <span>{formatDate(selectedApplication.end_date)}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Payment Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Amount Paid</label>
                  <span>{formatCurrency(selectedApplication.amount)}</span>
                </div>
                <div className="detail-item">
                  <label>Payment Mode</label>
                  <span>{selectedApplication.payment_mode || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Payment Sub Type</label>
                  <span>{selectedApplication.payment_sub_type || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Receipt No</label>
                  <span>{selectedApplication.receipt_no || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Transaction ID</label>
                  <span>{selectedApplication.transaction_id || "-"}</span>
                </div>
                <div className="detail-item">
                  <label>Payment Status</label>
                  <span
                    className={getPaymentStatusBadgeClass(
                      selectedApplication.payment_status,
                    )}
                  >
                    {selectedApplication.payment_status || "-"}
                  </span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Submitted By</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Volunteer Name</label>
                  <span>{selectedApplication.volunteer_name}</span>
                </div>
                <div className="detail-item">
                  <label>Volunteer Code</label>
                  <span>{selectedApplication.volunteer_code}</span>
                </div>
              </div>
            </div>

            {selectedApplication.remarks && (
              <div className="detail-section">
                <h4>Remarks</h4>
                <p className="remarks-text">{selectedApplication.remarks}</p>
              </div>
            )}

            {selectedApplication.proof && (
              <div className="detail-section">
                <h4>Payment Proof</h4>
                <a
                  href={selectedApplication.proof}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="proof-link"
                >
                  View Proof Document
                </a>
              </div>
            )}
          </div>
        ) : null}
      </GlobalModal>

      {/* Single Approve Modal */}
      <GlobalModal
        open={showApproveModal}
        onClose={() => !processing && setShowApproveModal(false)}
        title="Approve Application"
        footer={
          <div className="modal-footer">
            <GlobalButton
              variant="secondary"
              onClick={() => setShowApproveModal(false)}
              disabled={processing}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="primary"
              onClick={handleApprove}
              loading={processing}
            >
              Confirm Approval
            </GlobalButton>
          </div>
        }
      >
        <div className="approval-form">
          <p>
            Are you sure you want to approve the application{" "}
            <strong>{selectedApplication?.application_code}</strong> for{" "}
            <strong>{selectedApplication?.student_name}</strong>?
          </p>
          <div
            className={`form-field ${fieldErrors.remarks ? "has-error" : ""}`}
          >
            <label htmlFor="approve-remarks">
              Remarks <span className="optional">(Optional)</span>
            </label>
            <textarea
              id="approve-remarks"
              className={`gff-input ${fieldErrors.remarks ? "gff-input--error" : ""}`}
              rows="3"
              placeholder="Add any optional remarks..."
              value={approveRemarks}
              onChange={(e) => {
                setApproveRemarks(e.target.value);
                clearFieldError("remarks");
              }}
              disabled={processing}
            />
            {fieldErrors.remarks && (
              <span className="gff-error" role="alert">
                {fieldErrors.remarks}
              </span>
            )}
          </div>
        </div>
      </GlobalModal>

      {/* Single Reject Modal */}
      <GlobalModal
        open={showRejectModal}
        onClose={() => !processing && setShowRejectModal(false)}
        title="Reject Application"
        footer={
          <div className="modal-footer">
            <GlobalButton
              variant="secondary"
              onClick={() => setShowRejectModal(false)}
              disabled={processing}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleReject}
              loading={processing}
            >
              Confirm Rejection
            </GlobalButton>
          </div>
        }
      >
        <div className="rejection-form">
          <p>
            Are you sure you want to reject the application{" "}
            <strong>{selectedApplication?.application_code}</strong> for{" "}
            <strong>{selectedApplication?.student_name}</strong>?
          </p>
          <div
            className={`form-field ${fieldErrors.reason ? "has-error" : ""}`}
          >
            <label htmlFor="reject-reason">
              Reason <span className="required">*</span>
            </label>
            <textarea
              id="reject-reason"
              className={`gff-input ${fieldErrors.reason ? "gff-input--error" : ""}`}
              rows="3"
              placeholder="Enter the reason for rejection (minimum 5 characters)..."
              value={rejectReason}
              onChange={(e) => {
                setRejectReason(e.target.value);
                clearFieldError("reason");
              }}
              disabled={processing}
            />
            {fieldErrors.reason && (
              <span className="gff-error" role="alert">
                {fieldErrors.reason}
              </span>
            )}
            <small className="field-hint">
              This reason will be recorded and may be shared with the volunteer.
            </small>
          </div>
        </div>
      </GlobalModal>

      {/* Bulk Approve Modal */}
      <GlobalModal
        open={showBulkApproveModal}
        onClose={() => !processing && setShowBulkApproveModal(false)}
        title={`Approve ${selectedCount} Application${selectedCount !== 1 ? "s" : ""}`}
        footer={
          <div className="modal-footer">
            <GlobalButton
              variant="secondary"
              onClick={() => setShowBulkApproveModal(false)}
              disabled={processing}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="primary"
              onClick={handleBulkApprove}
              loading={processing}
            >
              Confirm Approval
            </GlobalButton>
          </div>
        }
      >
        <div className="approval-form">
          <p>
            Are you sure you want to approve{" "}
            <strong>{selectedCount}</strong> selected application
            {selectedCount !== 1 ? "s" : ""}?
          </p>
          <div
            className={`form-field ${fieldErrors.remarks ? "has-error" : ""}`}
          >
            <label htmlFor="bulk-approve-remarks">
              Remarks <span className="optional">(Optional)</span>
            </label>
            <textarea
              id="bulk-approve-remarks"
              className={`gff-input ${fieldErrors.remarks ? "gff-input--error" : ""}`}
              rows="3"
              placeholder="Add any optional remarks (will be applied to all selected applications)..."
              value={approveRemarks}
              onChange={(e) => {
                setApproveRemarks(e.target.value);
                clearFieldError("remarks");
              }}
              disabled={processing}
            />
            {fieldErrors.remarks && (
              <span className="gff-error" role="alert">
                {fieldErrors.remarks}
              </span>
            )}
          </div>
        </div>
      </GlobalModal>

      {/* Bulk Reject Modal */}
      <GlobalModal
        open={showBulkRejectModal}
        onClose={() => !processing && setShowBulkRejectModal(false)}
        title={`Reject ${selectedCount} Application${selectedCount !== 1 ? "s" : ""}`}
        footer={
          <div className="modal-footer">
            <GlobalButton
              variant="secondary"
              onClick={() => setShowBulkRejectModal(false)}
              disabled={processing}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleBulkReject}
              loading={processing}
            >
              Confirm Rejection
            </GlobalButton>
          </div>
        }
      >
        <div className="rejection-form">
          <p>
            Are you sure you want to reject{" "}
            <strong>{selectedCount}</strong> selected application
            {selectedCount !== 1 ? "s" : ""}?
          </p>
          <div
            className={`form-field ${fieldErrors.reason ? "has-error" : ""}`}
          >
            <label htmlFor="bulk-reject-reason">
              Reason <span className="required">*</span>
            </label>
            <textarea
              id="bulk-reject-reason"
              className={`gff-input ${fieldErrors.reason ? "gff-input--error" : ""}`}
              rows="3"
              placeholder="Enter the reason for rejection (minimum 5 characters, will be applied to all selected applications)..."
              value={rejectReason}
              onChange={(e) => {
                setRejectReason(e.target.value);
                clearFieldError("reason");
              }}
              disabled={processing}
            />
            {fieldErrors.reason && (
              <span className="gff-error" role="alert">
                {fieldErrors.reason}
              </span>
            )}
            <small className="field-hint">
              This reason will be recorded and may be shared with the volunteers.
            </small>
          </div>
        </div>
      </GlobalModal>
    </div>
  );
}
